import { zodResolver } from '@hookform/resolvers/zod'
import { TFunction } from 'i18next'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MessageTemplateType } from 'src/api/api.generated'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { z } from 'zod'

export default function useSendMessageTemplateForm({ onSubmit, guestEmail }: { onSubmit: (value: SendMessageTemplateFormType) => Promise<void>; guestEmail?: string }) {
  const customErrorMap = useCustomError()
  const { t } = useTranslation()

  z.setErrorMap(customErrorMap)

  const schemaTranslated = getSendCommunicationEmailFormSchema(t)

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SendMessageTemplateFormType>({ resolver: zodResolver(schemaTranslated) })

  useEffect(() => {
    setValue('sendTo', guestEmail ?? '', { shouldDirty: false })
  }, [guestEmail])

  const submitHandler: SubmitHandler<SendMessageTemplateFormType> = useCallback(
    async (data: SendMessageTemplateFormType) => {
      await onSubmit(data)
    },
    [onSubmit],
  )

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  const formValue = watch()

  return { control, errors, onFormSubmit, setValue, formValue }
}

function getSendCommunicationEmailFormSchema(t?: TFunction) {
  return z
    .object({
      sendTo: z.string().nullish(),
      messageTemplateId: z.string().refine((data) => Boolean(data.length), { message: t ? t('validation.required_error') : 'required' }),
      type: z.nativeEnum(MessageTemplateType),
    })
    .superRefine((data, context) => {
      if (data.type === MessageTemplateType.MAIL) {
        if (!data.sendTo) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sendTo'],
            message: t ? t('validation.required_error') : 'required',
          })
        } else if (!z.string().email().safeParse(data.sendTo).success) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sendTo'],
            message: t ? t('pms.send_message_template.invalid_email') : 'required',
          })
        }
      }
    })
}

const sendMessageTemplateSchema = getSendCommunicationEmailFormSchema()
export type SendMessageTemplateFormType = z.infer<typeof sendMessageTemplateSchema>
